import React, { useEffect, useState } from 'react';

import { Link } from 'react-router-dom';

import { useLogin } from '@/hooks/useLogin';
import { chromeFor } from '@shared/app-identity/appChrome';
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';
import { pendingCountForLastAccount } from '@shared/offline';

/**
 * Writes still queued for the account that was last signed in, read once on
 * mount and only when the browser reports no network. The login wall has no
 * session, so this is the one place a signed-out writer can learn their
 * changes are safe on this device.
 */
function useOfflinePendingCount(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (navigator.onLine) return undefined;
    let cancelled = false;
    void pendingCountForLastAccount()
      .then(pending => {
        if (!cancelled) setCount(pending);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return count;
}

/** The offline line under the sign-in prompt; nothing when nothing is waiting. */
function OfflinePendingLine({ count }: { count: number }): React.ReactElement | null {
  if (count <= 0) return null;
  const changes = count === 1 ? 'pending change is' : 'pending changes are';
  return (
    <p className="text-muted-foreground mt-2 text-center text-sm">
      You&apos;re offline. Sign in when you&apos;re back online; your {count} {changes} kept on
      this device.
    </p>
  );
}

const Login: React.FC = () => {
  const { loading, isRedirecting, error, handleLogin } = useLogin();
  // With per-app storage the user signs in once per app, so this screen is the
  // first thing each newly installed app shows.
  const installedApp = useInstalledApp();
  const chrome = chromeFor(installedApp);
  const offlinePending = useOfflinePendingCount();

  return (
    <div className="bg-muted flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div>
          {installedApp !== null && (
            // alt="" deliberately: the app name is already in the heading beside
            // it, so alt text would make a screen reader announce it twice.
            <img
              src={chrome.icon}
              alt=""
              data-testid="app-icon"
              className="mx-auto h-16 w-16 rounded-2xl"
            />
          )}
          {/* Short name for the same reason as the splash: this screen is inside
              the app, whose home-screen label already reads just "Tasks". */}
          <h2 className="text-foreground mt-6 text-center text-3xl font-light">
            Welcome to <strong>{chrome.shortName}</strong>
          </h2>
          <p className="text-muted-foreground mt-2 text-center text-base">Sign in to get started</p>
          <OfflinePendingLine count={offlinePending} />
        </div>

        {error && (
          <div className="rounded-sm border border-destructive bg-destructive-subtle p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-destructive-text"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-base font-medium text-destructive-text">
                  {error.message}
                </h3>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 space-y-6">
          <button
            onClick={handleLogin}
            disabled={loading || isRedirecting}
            className={`group relative flex w-full justify-center rounded-sm border-2 px-4 py-3 text-base font-medium transition-all duration-200 ease-in-out ${
              loading || isRedirecting
                ? 'border-border bg-muted text-tertiary-foreground cursor-not-allowed'
                : 'border-input bg-background text-foreground hover:border-muted-foreground hover:bg-muted shadow-xs hover:shadow-lg'
            } focus:border-muted-foreground focus:outline focus:outline-none`}
          >
            {loading ? (
              <div className="flex items-center">
                <svg
                  className="text-muted-foreground mr-3 -ml-1 h-5 w-5 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>Continue with Google</span>
              </div>
            )}
          </button>
        </div>

        <div className="flex items-center justify-center gap-4 text-sm">
          <Link to="/privacy" className="text-muted-foreground hover:text-foreground underline">
            Privacy Policy
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/terms" className="text-muted-foreground hover:text-foreground underline">
            Terms of Service
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
